import { Plus } from "lucide-react";
import React, { useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ClientAccountCreateDrawer } from "@/modules/dashboard/clients/components/accounts/ClientAccountCreateDrawer";
import type { ClientAccount } from "@/modules/dashboard/clients/types/clients-api.types";
import { canWriteClientAccounts } from "@/modules/dashboard/clients/utils/clientsPermissions";
import useTranslations from "@/shared/lang/useTranslations";

export interface RegisterAccountActionProps {
  /** Selected client the account is assigned to. */
  clientId: string;
  clientDisplayName: string;
  /** Destination currency of the dispersion — locks the account form to it. */
  destinationCurrency: string;
  /** Fired right after the drawer closes with the created account (select + autofill it). */
  onAccountCreated: (account: ClientAccount) => void;
}

/**
 * "Register account" shortcut for the recipient step — the right-aligned label action of the account select,
 * mirror of `RegisterClientAction`. Opens the same account form as /app/clients in a side drawer, locked to
 * the destination currency so the new account is usable in this dispersion immediately. Hidden without the
 * client-accounts write permission.
 */
export const RegisterAccountAction: React.FC<RegisterAccountActionProps> = ({
  clientId,
  clientDisplayName,
  destinationCurrency,
  onAccountCreated,
}) => {
  const { user } = useAuth();
  const { t } = useTranslations("dispersions");
  const [isOpen, setIsOpen] = useState(false);

  if (!canWriteClientAccounts(user)) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-primary/90"
      >
        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
        {t("register_account_button")}
      </button>
      <ClientAccountCreateDrawer
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        clientId={clientId}
        clientDisplayName={clientDisplayName}
        defaultCurrency={destinationCurrency}
        lockCurrency
        onCreated={(account) => {
          setIsOpen(false);
          onAccountCreated(account);
        }}
      />
    </>
  );
};
